import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { SessionService } from "./session.service";
import { NavController } from "@ionic/angular";

@Injectable({
  providedIn: "root",
})
export class ApiService {
  public version_android = environment.appVersionAndroid;
  public version_android_readable = environment.appVersionAndroidReadable;
  public version_ios = environment.appVersionIos;
  public version_ios_readable = environment.appVersionIosReadable;

  public apiurl: string = environment.apiUrl;
  public apikey: string = environment.apiKey;

  public usersession: any = null;
  public context: any = null;

  public assets: Array<any> = [];
  public searchedAssets: Array<any> = [];
  public wallets: Array<any> = [];
  public accounts: Array<any> = [];
  public balances: any = {};
  public filteredBalances: any = {};
  public portfolioBalance: number;

  public swapPairs: Array<any> = [];
  public prime: any;

  public selectedAccount: any;
  public selectedBlockchain: any;
  public selectedWallet: any;
  public selectedWalletIndex: number;

  public transactions: any = {};
  public fiatTransactions: any = {};
  public affiliates: any = {};
  public affiliateV2Info: any;
  public stakings: any = {};
  public stakingV2Asset: any;

  public totalAssetsCount: number = 0;

  public showFullLoader: boolean = true;
  public assets_loaded: boolean = false;

  public isBackButtonEvent: boolean = false;

  constructor(
    private http: HttpClient,
    private sessionService: SessionService,
    private navCtrl: NavController
  ) {
    this.loadSession();
  }

  /**
   * HTTP Post
   * @param endpoint
   * @param body
   * @param headers
   * @returns
   */
  public post(endpoint: string, body: any, headers: any): Promise<any> {
    return new Promise((resolve) => {
      this.http.post(endpoint, body, { headers }).subscribe(
        (result: any) => {
          resolve(result);
        },
        (error: any) => {
          if (error.error.code == 502) {
            this.logout();
          } else {
            //console.log(error.error);
            resolve(error.error);
          }
        }
      );
    });
  }

  /**
   * HTTP Get
   * @param endpoint
   * @param headers
   * @returns
   */
  public get(endpoint: string, headers: any): Promise<any> {
    return new Promise((resolve) => {
      this.http.get(endpoint, { headers }).subscribe(
        (result: any) => {
          resolve(result);
        },
        (error: any) => {
          if (error.error.code == 502) {
            this.logout();
          } else {
            resolve(error.error);
          }
        }
      );
    });
  }

  /**
   * Login to tronspace
   * @param email_id
   * @param password
   * @returns
   */
  public login(email_id: any, password: string, security_code?: string) {
    const endpoint = this.apiurl + "/api/auth/signin";
    const headers: HttpHeaders = new HttpHeaders().set("ApiKey", this.apikey);

    const body = {
      email_id: email_id,
      password: password,
      security_code: security_code || undefined,
    };

    return this.http.post(endpoint, body, { headers });
  }

  public resendLoginSecurityCode(email_id: string, password: string) {
    const endpoint = this.apiurl + "/api/auth/signin/otp/resend";
    const headers: HttpHeaders = new HttpHeaders().set("ApiKey", this.apikey);
    return this.http.post(endpoint, { email_id, password }, { headers });
  }

  /**
   * Register to wallet
   * @param data
   * @returns
   */
  public register(data: any) {
    const endpoint = this.apiurl + "/api/auth/register";
    const headers: HttpHeaders = new HttpHeaders().set("ApiKey", this.apikey);

    const body = {
      firstname: data.firstname,
      lastname: data.lastname,
      email_id: data.email_id,
      password: data.password,
      password_confirmation: data.password_confirmation,
      device_uuid: data.device_uuid,
      referrer_code: data.referrer_code || undefined,
    };

    return this.http.post(endpoint, body, { headers });
  }

  /**
   * Get Password OTP
   * @param email_id
   * @returns
   */
  public getPasswordOTP(email_id: string) {
    const endpoint = this.apiurl + "/api/auth/password/otp";
    const headers: HttpHeaders = new HttpHeaders().set("Apikey", this.apikey);

    const data = {
      email_id: email_id,
    };
    return this.http.post(endpoint, data, { headers }).pipe(
      (response: any) => response,
      (error: any) => error
    );
  }

  /**
   * Reset password
   * @param data
   * @returns
   */
  public resetPassword(data: any) {
    const endpoint = this.apiurl + "/api/auth/password";
    const headers: HttpHeaders = new HttpHeaders().set("Apikey", this.apikey);

    return this.http.post(endpoint, data, { headers }).pipe(
      (response: any) => response,
      (error: any) => error
    );
  }

  /**
   * Get transaction password OTP
   */
  public getTransactionPasswordOTP() {
    const endpoint = this.apiurl + "/api/user/txn/password/otpcode";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);
    return this.get(endpoint, headers);
  }

  /**
   * set Transaction password
   * @param data
   * @returns
   */
  public setTransactionPassword(data: any) {
    const endpoint = this.apiurl + "/api/user/txn/password";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.post(endpoint, data, headers);
  }

  /**
   * Get the current two-factor authentication status.
   */
  public getTwoFactorStatus() {
    const endpoint = this.apiurl + "/api/user/2fa";
    return this.get(endpoint, this.authHeaders());
  }

  /**
   * Generate a new authenticator secret after password confirmation.
   */
  public setupTwoFactor(password: string) {
    const endpoint = this.apiurl + "/api/user/2fa/setup";
    return this.post(endpoint, { password: password }, this.authHeaders());
  }

  /**
   * Confirm the authenticator code and enable 2FA.
   */
  public enableTwoFactor(code: string) {
    const endpoint = this.apiurl + "/api/user/2fa/enable";
    return this.post(endpoint, { code: code }, this.authHeaders());
  }

  /**
   * Disable 2FA after password and authenticator verification.
   */
  public disableTwoFactor(password: string, code: string) {
    const endpoint = this.apiurl + "/api/user/2fa/disable";
    const data = { password: password, code: code };
    return this.post(endpoint, data, this.authHeaders());
  }

  private authHeaders(): HttpHeaders {
    return new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);
  }

  /**
   * Verify email
   * @param data
   * @returns
   */
  public verifyEmail(data: any) {
    const endpoint = this.apiurl + "/api/auth/email/verify";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.post(endpoint, data, headers);
  }

  /**
   * Resend Email OTP
   * @returns
   */
  public resendEmailOTP() {
    const endpoint = this.apiurl + "/api/auth/otp/resend";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.get(endpoint, headers);
  }

  /**
   * Update Session information
   */
  public updateSesion() {
    this.sessionService.setSession(this.usersession).then((result) => {
      //console.log(this.usersession);
      return true;
    });
  }

  /**
   * Get user session
   * @returns
   */
  public getUserSession(): Promise<any> {
    if (this.usersession !== null) {
      return new Promise((resolve) => {
        resolve(this.usersession);
      });
    } else {
      return new Promise((resolve) => {
        this.sessionService.getSession().then((result: any) => {
          this.usersession = JSON.parse(result);
          resolve(this.usersession);
        });
      });
    }
  }

  /**
   * Load session
   */
  public loadSession() {
    this.sessionService.getSession().then((result: any) => {
      this.usersession = JSON.parse(result);
    });
  }

  /**
   * Set Session
   * @param data
   * @returns
   */
  public setSession(data: any): Promise<any> {
    this.usersession = data;
    return this.sessionService.setSession(data);
  }

  /**
   * Session loaded
   * @returns
   */
  public sessionLoaded(): Promise<any> {
    return new Promise((resolve) => {
      this.getUserSession().then((result: any) => {
        const session = result;
        if (
          session !== null &&
          session !== undefined &&
          session.accesstoken !== null
        ) {
          resolve(true);
        } else {
          console.log(session);
          //this.navCtrl.navigateRoot("");
        }
      });
    });
  }

  /**
   * Check if user can login
   */
  public canLogin() {
    this.getUserSession().then((result) => {
      const session = result;
      if (
        session !== null &&
        session !== undefined &&
        session.accesstoken !== null
      ) {
        this.navCtrl.navigateRoot("tabs/home");
      } else {
        this.showFullLoader = false;
        console.log("user is not logged in");
      }
    });
  }

  public clearAll() {
    this.usersession = null;
    this.context = null;

    this.assets = [];
    this.searchedAssets = [];
    this.wallets = [];
    this.accounts = [];
    this.balances = {};
    this.filteredBalances = {};
    this.portfolioBalance = 0;
    this.swapPairs = [];
    this.prime = null;
    this.selectedAccount = null;
    this.selectedBlockchain = null;
    this.selectedWallet = null;
    this.selectedWalletIndex = 0;
    this.transactions = {};
    this.fiatTransactions = {};
    this.affiliates = {};
    this.affiliateV2Info = null;
    this.stakings = {};
    this.stakingV2Asset = null;
    this.totalAssetsCount = 0;
    this.showFullLoader = false;
    this.assets_loaded = false;
    this.isBackButtonEvent = false;
  }

  /**
   * Logout
   */
  public logout() {
    const endpoint = this.apiurl + "/api/auth/signout";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    let data = {};
    this.http.post(endpoint, data, { headers }).subscribe(
      (result: any) => {
        this.sessionService.emptySession().then((result) => {
          this.clearAll();
          this.navCtrl.navigateRoot("");
        });
      },
      (error) => {
        //console.log(error);
        this.sessionService.emptySession().then((result) => {
          this.clearAll();
          this.navCtrl.navigateRoot("");
        });
      }
    );
  }

  /**
   * Update Password
   * @param data
   * @returns
   */
  public updatePassword(data: any) {
    const endpoint = this.apiurl + "/api/user/password/change";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.post(endpoint, data, headers);
  }

  /**
   * Update profile name
   * @param user_id
   * @param firstname
   * @param lastname
   * @returns
   */
  public updateProfileName(
    user_id: number,
    firstname: string,
    lastname: string
  ) {
    const endpoint = this.apiurl + "/api/user/update";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    let data = {
      user_id: user_id,
      firstname: firstname,
      lastname: lastname,
    };
    return this.post(endpoint, data, headers);
  }

  /**
   * Get Asset List
   * @returns
   */
  public getAssetsList() {
    const endpoint = this.apiurl + "/api/asset/list";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.get(endpoint, headers);
  }

  /**
   * Change asset visibility
   * @param data
   * @returns
   */
  public changeAssetVisibility(data: any) {
    const endpoint = this.apiurl + "/api/asset/visibility";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.post(endpoint, data, headers);
  }

  /**
   * Get Wallet List
   * @returns
   */
  public getWalletList() {
    const endpoint = this.apiurl + "/api/wallet/list";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.get(endpoint, headers);
  }

  /**
   * Get Balances
   * @returns
   */
  public getBalances() {
    const endpoint = this.apiurl + "/api/asset/balances";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.get(endpoint, headers);
  }

  /**
   * Get Wallet balance
   * @param wallet_id
   * @returns
   */
  getWalletBalance(wallet_id: number, asset_id: number) {
    const endpoint =
      this.apiurl +
      "/api/asset/balance?wallet_id=" +
      wallet_id +
      "&asset_id=" +
      asset_id;
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.get(endpoint, headers);
  }

  /**
   * Update Wallet Name
   * @param data
   * @returns
   */
  public updateWalletName(data: any) {
    const endpoint = this.apiurl + "/api/wallet/update";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.post(endpoint, data, headers);
  }

  /**
   * Link wallet to account
   * @param data
   * @returns
   */
  public linkAccount(data: any) {
    const endpoint = this.apiurl + "/api/account/link";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.post(endpoint, data, headers);
  }

  /**
   * Get accounts
   * @returns
   */
  public getAccounts() {
    const endpoint = this.apiurl + "/api/account/list";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.get(endpoint, headers);
  }

  /**
   * Create account
   * @returns
   */
  public createAccount(data: any) {
    const endpoint = this.apiurl + "/api/account/create";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.post(endpoint, data, headers);
  }

  /**
   * Get Version
   * @returns
   */
  public getVersion() {
    const endpoint = this.apiurl + "/api/version";
    const headers: HttpHeaders = new HttpHeaders().set("Apikey", this.apikey);

    return this.get(endpoint, headers);
  }

  /**
   * Create Wallet
   * @param data any
   * @returns
   */
  public createWallet(data: any) {
    const endpoint = this.apiurl + "/api/wallet/create";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.post(endpoint, data, headers);
  }

  /**
   * Get Pending Wallet Jobs
   * @param blockchain_id
   * @returns
   */
  public getPendingWalletJobs(blockchain_id: number) {
    const endpoint =
      this.apiurl + "/api/wallet/jobs/pending?blockchain_id=" + blockchain_id;
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.get(endpoint, headers);
  }

  /**
   * Get Prime Information
   * @returns
   */
  public getPrimeInfo() {
    const endpoint = this.apiurl + "/api/user/prime";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.get(endpoint, headers);
  }

  /**
   * Create V2 Affiliate
   * @param data
   * @returns
   */
  public createV2Affiliate(data: any) {
    const endpoint = this.apiurl + "/api/affiliate/v2/create";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.post(endpoint, data, headers);
  }

  /**
   * Get Wallet transactions
   * @param wallet_id
   * @param next
   * @param asset_id
   * @returns
   */
  public getWalletTransactions(wallet_id: number, next: any, asset_id: number) {
    const endpoint =
      this.apiurl +
      "/api/wallet/transactions?wallet_id=" +
      wallet_id +
      "&asset_id=" +
      asset_id +
      "&next=" +
      next;
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.get(endpoint, headers);
  }
  /**
   * Upload base64 image
   * @param data
   * @returns
   */
  public uploadImageBase64(data: string) {
    const endpoint = this.apiurl + "/api/file/base64";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    var body = {
      base64: data,
      crop: 1,
      width: 512,
      height: 512,
    };

    return this.post(endpoint, body, headers);
  }

  /**
   * Get wallet transactions
   * @param wallet_id
   * @param page
   * @returns
   */
  public getFiatTransactions(wallet_id: number, page: number) {
    const endpoint =
      this.apiurl +
      "/api/wallet/fiat/transactions?wallet_id=" +
      wallet_id +
      "&page=" +
      page;
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.get(endpoint, headers);
  }

  /**
   * Transfer Fiat
   * @param data
   * @returns
   */
  public transferFiat(data: any) {
    const endpoint = this.apiurl + "/api/wallet/fiat/transfer";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.post(endpoint, data, headers);
  }

  /**
   * Transfer Asset
   * @param data
   * @returns
   */
  public transferAsset(data: any) {
    const endpoint = this.apiurl + "/api/asset/transfer";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.post(endpoint, data, headers);
  }

  /**
   * Get Swapping Paris
   * @returns
   */
  public getSwapPairs() {
    const endpoint = this.apiurl + "/api/swap/pairs";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.get(endpoint, headers);
  }

  /**
   * Swap Asset
   * @param data
   * @returns
   */
  public swapAsset(data: any) {
    const endpoint = this.apiurl + "/api/swap/asset";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.post(endpoint, data, headers);
  }

  /**
   * Get Affiliate
   * @param wallet_id
   * @returns
   */
  public getAffiliate(wallet_id: number) {
    const endpoint =
      this.apiurl + "/api/affiliate/v1/info?wallet_id=" + wallet_id;
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.get(endpoint, headers);
  }

  /**
   * Get V2 Affilifte
   * @returns
   */
  public getv2Affiliate() {
    const endpoint = this.apiurl + "/api/affiliate/v2/info";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.get(endpoint, headers);
  }

  /**
   * Get V2 Transactions
   * @param page
   * @returns
   */
  public getV2Transactions(page: number) {
    const endpoint = this.apiurl + "/api/staking/v2/transactions?page=" + page;
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.get(endpoint, headers);
  }

  /**
   * Get staking v2 income
   * @param page
   * @returns
   */
  public getV2Income(page: number) {
    const endpoint = this.apiurl + "/api/staking/v2/income?page=" + page;
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.get(endpoint, headers);
  }

  /**
   * Get V2 Withdrawals
   * @param page
   * @returns
   */
  public getV2Withdrawals(page: number) {
    const endpoint = this.apiurl + "/api/staking/v2/withdrawal?page=" + page;
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.get(endpoint, headers);
  }

  /**
   * Withdraw Kaudi
   * @param data
   * @returns
   */
  public withdrawKaudi(data: any) {
    const endpoint = this.apiurl + "/api/staking/v2/withdraw";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.post(endpoint, data, headers);
  }

  /**
   * Get all staking packages
   * @param wallet_id
   * @returns any
   */
  public getStakings(wallet_id: number) {
    const endpoint =
      this.apiurl + "/api/staking/v1/list?wallet_id=" + wallet_id;
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.get(endpoint, headers);
  }

  /**
   * Get V2 Stakings
   * @returns
   */
  public getV2Stakings() {
    const endpoint = this.apiurl + "/api/staking/v2/list";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.get(endpoint, headers);
  }

  /**
   * Get V2 Staking Asset
   * @returns
   */
  public getV2StakingAsset() {
    const endpoint = this.apiurl + "/api/staking/v2/asset";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.get(endpoint, headers);
  }

  /**
   * Get available staking poackages
   * @returns
   */
  public getStakingPackages() {
    const endpoint = this.apiurl + "/api/staking/packages";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.get(endpoint, headers);
  }

  /**
   * Add V2 Staking
   * @param data
   * @returns
   */
  public addV2Staking(data: any) {
    const endpoint = this.apiurl + "/api/staking/v2/create";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.post(endpoint, data, headers);
  }

  /**
   * Send fiat to bank
   * @param data
   * @returns
   */
  public sendFiatToBank(data: any) {
    const endpoint = this.apiurl + "/api/wallet/fiat/withdraw";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.post(endpoint, data, headers);
  }

  /**
   * Withdraw USDT from a Prime V1 balance to a BEP-20 address.
   * @param data
   * @returns
   */
  public withdrawUSDT(data: any) {
    const endpoint = this.apiurl + "/api/wallet/usdt/withdraw";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.post(endpoint, data, headers);
  }

  /**
   * Buy Staking Package
   * @param data
   * @returns
   */
  public buyStakingPackage(data: any) {
    const endpoint = this.apiurl + "/api/staking/add";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.post(endpoint, data, headers);
  }

  /**
   * Get admin dashboard
   * @returns
   */
  public getDashboard() {
    const endpoint = this.apiurl + "/api/admin/metrics";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.get(endpoint, headers);
  }

  /**
   * Process pending transactions
   * @returns
   */
  public processPendingImps() {
    let process_token = "testtoken";
    const endpoint =
      this.apiurl +
      "/api/process/imps/transactions?process_token=" +
      process_token;
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.get(endpoint, headers);
  }

  /**
   * Get/Search Wallets only admin
   * @param search
   * @param page
   * @returns
   */
  public getAdminWallets(search: string, page: number) {
    const endpoint =
      this.apiurl +
      "/api/admin/wallet/search?page=" +
      page +
      "&search=" +
      search;

    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.get(endpoint, headers);
  }

  /**
   * Get wallet by id only admin
   * @param wallet_id
   * @returns
   */
  public getAdminWallet(wallet_id: number) {
    const endpoint =
      this.apiurl + "/api/admin/wallet/get?wallet_id=" + wallet_id;

    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.get(endpoint, headers);
  }

  /** Set the remaining USDT withdrawal limit for a Prime staking wallet. */
  public updateAdminWalletUSDTLimit(data: any) {
    const endpoint = this.apiurl + "/api/admin/wallet/usdt-limit";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.post(endpoint, data, headers);
  }

  /**
   * Get/Search users only admin
   * @param search
   * @param page
   * @returns
   */
  public getAdminUsers(search: string, page: number) {
    const endpoint =
      this.apiurl + "/api/admin/user/search?page=" + page + "&search=" + search;

    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.get(endpoint, headers);
  }

  /**
   * Get/Search accounts only admin
   * @param search
   * @param page
   * @returns
   */
  public getAdminAccounts(search: string, page: number) {
    const endpoint =
      this.apiurl +
      "/api/admin/account/search?page=" +
      page +
      "&search=" +
      search;

    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.get(endpoint, headers);
  }

  /**
   * Update IMPS limit
   * @param data
   * @returns
   */
  public updateIMPSLimit(data: any) {
    const endpoint = this.apiurl + "/api/admin/imps/limit";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.post(endpoint, data, headers);
  }

  /**
   * Admin Withdraw Fiat
   * @param data
   * @returns
   */
  public adminWithdrawFiat(data: any) {
    const endpoint = this.apiurl + "/api/admin/wallet/withdraw";
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.post(endpoint, data, headers);
  }

  /**
   * Get staking backage by package id
   * @param package_id
   * @returns
   */
  public getStakingPackage(package_id: number) {
    const endpoint =
      this.apiurl + "/api/staking/package?package_id=" + package_id;
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.get(endpoint, headers);
  }

  /** Get one Prime V1 staking package for a wallet. */
  public getV1Staking(wallet_id: number, package_id: number) {
    const endpoint =
      this.apiurl +
      "/api/staking/v1/get?wallet_id=" +
      wallet_id +
      "&package_id=" +
      package_id;
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.get(endpoint, headers);
  }

  /**
   * Get Staking Certificate
   * @param package_id
   * @returns
   */
  public getStakingCertificate(package_id: number) {
    const endpoint =
      this.apiurl + "/api/staking/certificate?package_id=" + package_id;
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.get(endpoint, headers);
  }

  /**
   * Get Referrer
   * @param device_uuid
   * @returns
   */
  public getReferrer(device_uuid: string) {
    const endpoint =
      this.apiurl + "/api/user/referrer?device_uuid=" + device_uuid;
    const headers: HttpHeaders = new HttpHeaders()
      .set("Apikey", this.apikey)
      .set("Authorization", "Bearer " + this.usersession.accesstoken);

    return this.get(endpoint, headers);
  }
}
